"use client";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useSWR } from "../utils/trigger-swr.js";
import { useApiClient } from "./useApiClient.js";
import { createThrottledQueue } from "../utils/throttle.js";
/**
 * Hook to subscribe to realtime updates of a task run.
 *
 * @template TTask - The type of the task
 * @param {string} [runId] - The unique identifier of the run to subscribe to
 * @param {UseRealtimeSingleRunOptions} [options] - Configuration options for the subscription
 * @returns {UseRealtimeRunInstance<TTask>} An object containing the current state of the run, error handling, and control methods
 *
 * @example
 * ```ts
 * import type { myTask } from './path/to/task';
 * const { run, error } = useRealtimeRun<typeof myTask>('run-id-123');
 * ```
 */
export function useRealtimeRun(runId, options) {
    const hookId = useId();
    const idKey = options?.id ?? hookId;
    // Store the streams state in SWR, using the idKey as the key to share states.
    const { data: run, mutate: mutateRun } = useSWR([idKey, "run"], null);
    const { data: error = undefined, mutate: setError } = useSWR([idKey, "error"], null);
    // Add state to track when the subscription is complete
    const { data: isComplete = false, mutate: setIsComplete } = useSWR([idKey, "complete"], null);
    // Abort controller to cancel the current API call.
    const abortControllerRef = useRef(null);
    const stop = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
    }, []);
    const apiClient = useApiClient(options);
    const triggerRequest = useCallback(async () => {
        try {
            if (!runId || !apiClient) {
                return;
            }
            const abortController = new AbortController();
            abortControllerRef.current = abortController;
            await processRealtimeRun(runId, apiClient, mutateRun, setError, abortControllerRef, typeof options?.stopOnCompletion === "boolean" ? options.stopOnCompletion : true);
        }
        catch (err) {
            // Ignore abort errors as they are expected.
            if (err.name === "AbortError") {
                abortControllerRef.current = null;
                return;
            }
            setError(err);
        }
        finally {
            if (abortControllerRef.current) {
                abortControllerRef.current = null;
            }
            // Mark the subscription as complete
            setIsComplete(true);
        }
    }, [runId, mutateRun, abortControllerRef, apiClient, setError]);
    const hasCalledOnCompleteRef = useRef(false);
    // Effect to handle onComplete callback
    useEffect(() => {
        if (isComplete && run && options?.onComplete && !hasCalledOnCompleteRef.current) {
            options.onComplete(run, error);
            hasCalledOnCompleteRef.current = true;
        }
    }, [isComplete, run, error, options?.onComplete]);
    useEffect(() => {
        if (typeof options?.enabled === "boolean" && !options.enabled) {
            return;
        }
        if (!runId) {
            return;
        }
        triggerRequest().finally(() => { });
        return () => {
            stop();
        };
    }, [runId, stop, options?.enabled]);
    useEffect(() => {
        if (run?.finishedAt) {
            setIsComplete(true);
        }
    }, [run]);
    return { run, error, stop };
}
/**
 * Hook to subscribe to realtime updates of a task run with associated data streams.
 *
 * @template TTask - The type of the task
 * @template TStreams - The type of the streams data
 * @param {string} [runId] - The unique identifier of the run to subscribe to
 * @param {UseRealtimeRunOptions} [options] - Configuration options for the subscription
 * @returns {UseRealtimeRunWithStreamsInstance<TTask, TStreams>} An object containing the current state of the run, streams data, and error handling
 *
 * @example
 * ```ts
 * import type { myTask } from './path/to/task';
 * const { run, streams, error } = useRealtimeRunWithStreams<typeof myTask, {
 *   output: string;
 * }>('run-id-123');
 * ```
 */
export function useRealtimeRunWithStreams(runId, options) {
    const hookId = useId();
    const idKey = options?.id ?? hookId;
    const [initialStreamsFallback] = useState({});
    // Store the streams state in SWR, using the idKey as the key to share states.
    const { data: streams, mutate: mutateStreams } = useSWR([idKey, "streams"], null, {
        fallbackData: initialStreamsFallback,
    });
    // Keep the latest streams in a ref.
    const streamsRef = useRef(streams ?? {});
    useEffect(() => {
        streamsRef.current = streams || {};
    }, [streams]);
    // Store the streams state in SWR, using the idKey as the key to share states.
    const { data: run, mutate: mutateRun } = useSWR([idKey, "run"], null);
    // Add state to track when the subscription is complete
    const { data: isComplete = false, mutate: setIsComplete } = useSWR([idKey, "complete"], null);
    const { data: error = undefined, mutate: setError } = useSWR([idKey, "error"], null);
    // Abort controller to cancel the current API call.
    const abortControllerRef = useRef(null);
    const stop = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
    }, []);
    const apiClient = useApiClient(options);
    const triggerRequest = useCallback(async () => {
        try {
            if (!runId || !apiClient) {
                return;
            }
            const abortController = new AbortController();
            abortControllerRef.current = abortController;
            await processRealtimeRunWithStreams(runId, apiClient, mutateRun, mutateStreams, streamsRef, setError, abortControllerRef, typeof options?.stopOnCompletion === "boolean" ? options.stopOnCompletion : true, options?.experimental_throttleInMs);
        }
        catch (err) {
            // Ignore abort errors as they are expected.
            if (err.name === "AbortError") {
                abortControllerRef.current = null;
                return;
            }
            setError(err);
        }
        finally {
            if (abortControllerRef.current) {
                abortControllerRef.current = null;
            }
            // Mark the subscription as complete
            setIsComplete(true);
        }
    }, [runId, mutateRun, mutateStreams, streamsRef, abortControllerRef, apiClient, setError]);
    const hasCalledOnCompleteRef = useRef(false);
    // Effect to handle onComplete callback
    useEffect(() => {
        if (isComplete && run && options?.onComplete && !hasCalledOnCompleteRef.current) {
            options.onComplete(run, error);
            hasCalledOnCompleteRef.current = true;
        }
    }, [isComplete, run, error, options?.onComplete]);
    useEffect(() => {
        if (typeof options?.enabled === "boolean" && !options.enabled) {
            return;
        }
        if (!runId) {
            return;
        }
        triggerRequest().finally(() => { });
        return () => {
            stop();
        };
    }, [runId, stop, options?.enabled]);
    useEffect(() => {
        if (run?.finishedAt) {
            setIsComplete(true);
        }
    }, [run]);
    return { run, streams: streams ?? initialStreamsFallback, error, stop };
}
/**
 * Hook to subscribe to realtime updates of task runs filtered by tag(s).
 *
 * @template TTask - The type of the task
 * @param {string | string[]} tag - The tag or array of tags to filter runs by
 * @param {UseRealtimeRunOptions} [options] - Configuration options for the subscription
 * @returns {UseRealtimeRunsInstance<TTask>} An object containing the current state of the runs and any error encountered
 *
 * @example
 * ```ts
 * import type { myTask } from './path/to/task';
 * const { runs, error } = useRealtimeRunsWithTag<typeof myTask>('my-tag');
 * // Or with multiple tags
 * const { runs, error } = useRealtimeRunsWithTag<typeof myTask>(['tag1', 'tag2']);
 * ```
 */
export function useRealtimeRunsWithTag(tag, options) {
    const hookId = useId();
    const idKey = options?.id ?? hookId;
    // Store the streams state in SWR, using the idKey as the key to share states.
    const { data: runs, mutate: mutateRuns } = useSWR([idKey, "run"], null, {
        fallbackData: [],
    });
    // Keep the latest streams in a ref.
    const runsRef = useRef([]);
    useEffect(() => {
        runsRef.current = runs ?? [];
    }, [runs]);
    const { data: error = undefined, mutate: setError } = useSWR([idKey, "error"], null);
    // Abort controller to cancel the current API call.
    const abortControllerRef = useRef(null);
    const stop = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
    }, []);
    const apiClient = useApiClient(options);
    const triggerRequest = useCallback(async () => {
        try {
            if (!apiClient) {
                return;
            }
            const abortController = new AbortController();
            abortControllerRef.current = abortController;
            await processRealtimeRunsWithTag(tag, apiClient, mutateRuns, runsRef, setError, abortControllerRef);
        }
        catch (err) {
            // Ignore abort errors as they are expected.
            if (err.name === "AbortError") {
                abortControllerRef.current = null;
                return;
            }
            setError(err);
        }
        finally {
            if (abortControllerRef.current) {
                abortControllerRef.current = null;
            }
        }
    }, [tag, mutateRuns, runsRef, abortControllerRef, apiClient, setError]);
    useEffect(() => {
        if (typeof options?.enabled === "boolean" && !options.enabled) {
            return;
        }
        triggerRequest().finally(() => { });
        return () => {
            stop();
        };
    }, [tag, stop, options?.enabled]);
    return { runs: runs ?? [], error, stop };
}
/**
 * Hook to subscribe to realtime updates of a batch of task runs.
 *
 * @template TTask - The type of the task
 * @param {string} batchId - The unique identifier of the batch to subscribe to
 * @param {UseRealtimeRunOptions} [options] - Configuration options for the subscription
 * @returns {UseRealtimeRunsInstance<TTask>} An object containing the current state of the runs, error handling, and control methods
 *
 * @example
 * ```ts
 * import type { myTask } from './path/to/task';
 * const { runs, error } = useRealtimeBatch<typeof myTask>('batch-id-123');
 * ```
 */
export function useRealtimeBatch(batchId, options) {
    const hookId = useId();
    const idKey = options?.id ?? hookId;
    // Store the streams state in SWR, using the idKey as the key to share states.
    const { data: runs, mutate: mutateRuns } = useSWR([idKey, "run"], null, {
        fallbackData: [],
    });
    // Keep the latest streams in a ref.
    const runsRef = useRef([]);
    useEffect(() => {
        runsRef.current = runs ?? [];
    }, [runs]);
    const { data: error = undefined, mutate: setError } = useSWR([idKey, "error"], null);
    // Abort controller to cancel the current API call.
    const abortControllerRef = useRef(null);
    const stop = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
    }, []);
    const apiClient = useApiClient(options);
    const triggerRequest = useCallback(async () => {
        try {
            if (!apiClient) {
                return;
            }
            const abortController = new AbortController();
            abortControllerRef.current = abortController;
            await processRealtimeBatch(batchId, apiClient, mutateRuns, runsRef, setError, abortControllerRef);
        }
        catch (err) {
            // Ignore abort errors as they are expected.
            if (err.name === "AbortError") {
                abortControllerRef.current = null;
                return;
            }
            setError(err);
        }
        finally {
            if (abortControllerRef.current) {
                abortControllerRef.current = null;
            }
        }
    }, [batchId, mutateRuns, runsRef, abortControllerRef, apiClient, setError]);
    useEffect(() => {
        if (typeof options?.enabled === "boolean" && !options.enabled) {
            return;
        }
        triggerRequest().finally(() => { });
        return () => {
            stop();
        };
    }, [batchId, stop, options?.enabled]);
    return { runs: runs ?? [], error, stop };
}
async function processRealtimeBatch(batchId, apiClient, mutateRunsData, existingRunsRef, onError, abortControllerRef) {
    const subscription = apiClient.subscribeToBatch(batchId, {
        signal: abortControllerRef.current?.signal,
        onFetchError: onError,
    });
    for await (const part of subscription) {
        mutateRunsData(insertRunShapeInOrder(existingRunsRef.current, part));
    }
}
// Inserts and then orders by the run number, and ensures that the run is not duplicated
function insertRunShapeInOrder(previousRuns, run) {
    const existingRun = previousRuns.find((r) => r.id === run.id);
    if (existingRun) {
        return previousRuns.map((r) => (r.id === run.id ? run : r));
    }
    const runNumber = run.number;
    const index = previousRuns.findIndex((r) => r.number > runNumber);
    if (index === -1) {
        return [...previousRuns, run];
    }
    return [...previousRuns.slice(0, index), run, ...previousRuns.slice(index)];
}
async function processRealtimeRunsWithTag(tag, apiClient, mutateRunsData, existingRunsRef, onError, abortControllerRef) {
    const subscription = apiClient.subscribeToRunsWithTag(tag, {
        signal: abortControllerRef.current?.signal,
        onFetchError: onError,
    });
    for await (const part of subscription) {
        mutateRunsData(insertRunShape(existingRunsRef.current, part));
    }
}
// Replaces or inserts a run shape, ordered by the createdAt timestamp
function insertRunShape(previousRuns, run) {
    const existingRun = previousRuns.find((r) => r.id === run.id);
    if (existingRun) {
        return previousRuns.map((r) => (r.id === run.id ? run : r));
    }
    const createdAt = run.createdAt;
    const index = previousRuns.findIndex((r) => r.createdAt > createdAt);
    if (index === -1) {
        return [...previousRuns, run];
    }
    return [...previousRuns.slice(0, index), run, ...previousRuns.slice(index)];
}
async function processRealtimeRunWithStreams(runId, apiClient, mutateRunData, mutateStreamData, existingDataRef, onError, abortControllerRef, stopOnCompletion = true, throttleInMs) {
    const subscription = apiClient.subscribeToRun(runId, {
        signal: abortControllerRef.current?.signal,
        closeOnComplete: stopOnCompletion,
        onFetchError: onError,
    });
    const streamQueue = createThrottledQueue(async (updates) => {
        const nextStreamData = { ...existingDataRef.current };
        // Group updates by type
        const updatesByType = updates.reduce((acc, update) => {
            if (!acc[update.type]) {
                acc[update.type] = [];
            }
            acc[update.type].push(update.chunk);
            return acc;
        }, {});
        // Apply all updates
        for (const [type, chunks] of Object.entries(updatesByType)) {
            // @ts-ignore
            nextStreamData[type] = [...(existingDataRef.current[type] || []), ...chunks];
        }
        mutateStreamData(nextStreamData);
    }, throttleInMs);
    for await (const part of subscription.withStreams()) {
        if (part.type === "run") {
            mutateRunData(part.run);
        }
        else {
            streamQueue.add({
                type: part.type,
                // @ts-ignore
                chunk: part.chunk,
            });
        }
    }
}
async function processRealtimeRun(runId, apiClient, mutateRunData, onError, abortControllerRef, stopOnCompletion = true) {
    const subscription = apiClient.subscribeToRun(runId, {
        signal: abortControllerRef.current?.signal,
        closeOnComplete: stopOnCompletion,
        onFetchError: onError,
    });
    for await (const part of subscription) {
        mutateRunData(part);
    }
}
//# sourceMappingURL=useRealtime.js.map